import type { components } from '@feedfm/clips-services-api';
import { ArrowBack, ContentCopy, Pause, PlayArrow } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Fab,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CollectionThumbnail } from '../../../components/CollectionThumbnail';
import { Explicitness } from '../../../components/Explicitness';
import { LazyThumbnail } from '../../../components/LazyThumbnail';
import { Rating } from '../../../components/Rating';
import { SortByDropdown } from '../../../components/SortByDropdown';
import { useStudioAuthenticatedContext } from '../../../context/StudioContext';
import { useCopyToClipboard } from '../../../hooks/useCopyToClipboard';
import { StudioPage } from '../../../page-templates/StudioPage';
import { CollectionSortBy, sortCollection } from '../../../util/collectionSort';
import * as styles from './CollectionDetail.styles';

type Collection = components['schemas']['Collection'];
type ArtworkPreSignedUrls = components['schemas']['ArtworkPreSignedUrls'];

const CopyIdButton = ({ value, label }: { value: number; label: string }) => {
  const { copyToClipboard } = useCopyToClipboard();
  return (
    <Tooltip title={`Copy ${label}`} placement="top" arrow>
      <Button
        variant="outlined"
        size="small"
        startIcon={<ContentCopy />}
        onClick={() => copyToClipboard(value.toString(), label)}
        aria-label={`Copy ${label} ${value} to clipboard`}
      >
        {value}
      </Button>
    </Tooltip>
  );
};

export const CollectionDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { backendClient, playerState } = useStudioAuthenticatedContext();
  const { copyToClipboard } = useCopyToClipboard();

  // Get the previous page from state or default to collections
  const previousPage = location.state?.from || '/collections';

  const [collection, setCollection] = React.useState<Collection | null>(null);
  const [artworkPreSignedUrls, setArtworkPreSignedUrls] =
    React.useState<ArtworkPreSignedUrls | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const { collectionSort, setCollectionSort } = useStudioAuthenticatedContext();

  // Check if this collection is currently playing
  const isPlayingCollection =
    collection &&
    playerState.isCollectionPlaying(collection.collectionId) &&
    playerState.playIntent;

  React.useEffect(() => {
    const fetchCollection = async () => {
      setLoading(true);
      setError(null);

      if (!id) {
        setError('Collection ID is required');
        setLoading(false);
        return;
      }

      const collectionId = Number(id);
      if (isNaN(collectionId)) {
        setError('Invalid collection ID');
        setLoading(false);
        return;
      }

      try {
        const response = await backendClient.collectionById(collectionId);
        setCollection(response.collection);
        setArtworkPreSignedUrls(response.artworkPreSignedUrls);
        setThumbnailUrl(response.thumbnailUrl ?? null);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'An error occurred while fetching the collection',
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCollection();
  }, [id, backendClient]);

  const handleBack = () => {
    navigate(previousPage);
  };

  // Create a version of the collection whose clips are ordered according to the current sort
  const getCollectionInCurrentSortOrder = (): Collection | null => {
    if (!collection) return null;
    return { ...collection, clips: getSortedClips() } as Collection;
  };

  const handlePlayTrack = (sortedIndex: number) => {
    if (!collection) return;
    playerState.clickCollectionClip(collection, sortedIndex, collectionSort);
  };

  const handlePlayAll = () => {
    if (!collection) return;
    playerState.clickCollection(collection, collectionSort);
  };

  const handleCopyId = () => {
    if (!collection) return;
    copyToClipboard(collection.collectionId.toString(), 'Collection ID');
  };

  // Helper function to format duration from milliseconds to MM:SS (matching Global Audio Player)
  const formatDuration = (milliseconds: number): string => {
    const seconds = Math.floor(milliseconds / 1000);
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Helper function to format date
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  // Helper function to check if clip is new (less than 30 days old)
  const isClipNew = (createDate: string): boolean => {
    const clipDate = new Date(createDate);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return clipDate > thirtyDaysAgo;
  };

  // Helper function to sort clips based on current collectionSort state
  const getSortedClips = () => {
    if (!collection?.clips) return [];
    return sortCollection(collection, collectionSort).clips;
  };

  if (loading) {
    return (
      <StudioPage>
        <Box sx={styles.loadingContainer}>
          <CircularProgress />
        </Box>
      </StudioPage>
    );
  }

  if (error || !collection || !artworkPreSignedUrls) {
    return (
      <StudioPage>
        <Box sx={styles.errorContainer}>
          <Typography variant="h6" color="error">
            {error || 'Collection not found'}
          </Typography>
          <Button onClick={handleBack} variant="outlined">
            Go Back
          </Button>
        </Box>
      </StudioPage>
    );
  }

  return (
    <StudioPage>
      {/* Header */}
      <Box component="header" sx={styles.headerContainer}>
        <Box sx={styles.headerTopRow}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBack}
            sx={styles.backButton}
          >
            Back
          </Button>

          <Tooltip
            title="Copy collection ID to clipboard"
            placement="top"
            arrow
          >
            <Button
              variant="contained"
              startIcon={<ContentCopy />}
              onClick={handleCopyId}
              sx={styles.idButton}
              aria-label={`Copy collection ID ${collection.collectionId} to clipboard`}
            >
              ID: {collection.collectionId}
            </Button>
          </Tooltip>
        </Box>

        {/* Collection Info Card */}
        <Paper sx={styles.collectionInfoCard}>
          <Box sx={styles.collectionInfoContainer}>
            <CollectionThumbnail
              collection={collection}
              artworkPreSignedUrls={artworkPreSignedUrls}
              thumbnailUrl={thumbnailUrl}
              size="xlarge"
              showPlayButton={false}
            />

            <Fab
              sx={styles.playFab}
              onClick={handlePlayAll}
              aria-label={isPlayingCollection ? 'Pause' : 'Play'}
            >
              {isPlayingCollection ? (
                <Pause sx={styles.playFabIcon} />
              ) : (
                <PlayArrow sx={styles.playFabIcon} />
              )}
            </Fab>

            <Box sx={styles.collectionDetailsContainer}>
              <Box sx={styles.collectionTitleRow}>
                <Box>
                  <Box
                    sx={{
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    {collection.rating && <Rating rating={collection.rating} />}
                    {collection.explicit && (
                      <Explicitness explicitness="explicit" />
                    )}
                  </Box>
                  <Typography
                    variant="h4"
                    color="text.primary"
                    sx={styles.collectionTitle}
                  >
                    {collection.collectionName}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={styles.collectionDescription}
                  >
                    {collection.description}
                  </Typography>
                  {collection.publishedOn && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Published {formatDate(collection.publishedOn)}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Tracks Table */}
      <Container maxWidth={false} sx={styles.tracksContainer}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <SortByDropdown
            value={collectionSort}
            onChange={(e) =>
              setCollectionSort(e.target.value as CollectionSortBy)
            }
          >
            <MenuItem value={CollectionSortBy.CREATE_DATE}>Date Added</MenuItem>
            <MenuItem value={CollectionSortBy.NAME}>Name</MenuItem>
          </SortByDropdown>
        </Box>
        <TableContainer component={Paper} sx={styles.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={styles.tableHeaderCell}>Clips</TableCell>
                <TableCell sx={styles.tableHeaderCell}>Album</TableCell>
                <TableCell sx={styles.tableHeaderCell}>Duration</TableCell>
                <TableCell sx={styles.tableHeaderCell}>Explicitness</TableCell>
                <TableCell sx={styles.tableHeaderCell}>Track ID</TableCell>
                <TableCell sx={styles.tableHeaderCell}>Clip ID</TableCell>
                <TableCell sx={styles.tableHeaderCell}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getSortedClips().map((clip, index) => (
                <TableRow key={clip.clipId} hover sx={styles.tableRow}>
                  <TableCell sx={styles.trackInfoCell}>
                    <Box sx={styles.trackInfoContainer}>
                      <Box sx={styles.trackArtworkContainer}>
                        <LazyThumbnail
                          src={clip.artworkThumbnailUrl}
                          fallbackSrc={clip.artworkPreSignedUrl}
                          alt="Album cover"
                          sx={styles.trackArtworkImage}
                          className="track-artwork"
                          placeholder={
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              No Art
                            </Typography>
                          }
                        />

                        <IconButton
                          className="play-button"
                          size="small"
                          onClick={() => handlePlayTrack(index)}
                          sx={
                            playerState.isClipPlaying(clip.clipId) &&
                            playerState.playIntent
                              ? styles.playButtonActive
                              : styles.playButton
                          }
                        >
                          {playerState.isClipPlaying(clip.clipId) &&
                          playerState.playIntent ? (
                            <Pause sx={styles.playButtonIcon} />
                          ) : (
                            <PlayArrow sx={styles.playButtonIcon} />
                          )}
                        </IconButton>
                      </Box>
                      <Box>
                        <Typography
                          variant="body1"
                          color="text.primary"
                          sx={styles.trackTitle}
                        >
                          {clip.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {clip.artist}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell sx={styles.albumCell}>
                    <Typography variant="body1" color="text.primary">
                      {clip.album}
                    </Typography>
                  </TableCell>

                  <TableCell sx={styles.durationCell}>
                    <Typography variant="body1" color="text.primary">
                      {formatDuration(clip.duration)}
                    </Typography>
                  </TableCell>

                  <TableCell sx={styles.explicitnessCell}>
                    {clip.explicitness && (
                      <Explicitness explicitness={clip.explicitness} />
                    )}
                  </TableCell>

                  <TableCell sx={styles.copyIdsCell}>
                    <CopyIdButton value={clip.trackId} label="Track ID" />
                  </TableCell>
                  <TableCell sx={styles.copyIdsCell}>
                    <CopyIdButton value={clip.clipId} label="Clip ID" />
                  </TableCell>

                  <TableCell sx={styles.moreInfoCell}>
                    {isClipNew(clip.createDate) && (
                      <Chip
                        label="New"
                        size="small"
                        variant="outlined"
                        sx={styles.newChip}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </StudioPage>
  );
};
